import { supabase, isSupabaseConfigured } from './supabase'

// Sign up with email and password
export const signUp = async (email, password) => {
    if (!isSupabaseConfigured()) {
        return { data: null, error: { message: 'Supabase not configured' } }
    }

    const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`
        }
    })

    return { data, error }
}

// Sign in with email and password
export const signIn = async (email, password) => {
    if (!isSupabaseConfigured()) {
        return { data: null, error: { message: 'Supabase not configured' } }
    }

    const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
    })

    return { data, error }
}

// Sign out
export const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
}

// Get current user
export const getUser = async () => {
    if (!isSupabaseConfigured()) {
        return null
    }
    const { data: { user } } = await supabase.auth.getUser()
    return user
}

// Get current session
export const getSession = async () => {
    if (!isSupabaseConfigured()) {
        return null
    }
    const { data: { session } } = await supabase.auth.getSession()
    return session
}

// Password reset request
export const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`
    })

    return { data, error }
}

// Update password
export const updatePassword = async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({
        password: newPassword
    })

    return { data, error }
}

// Check if user is Pro (active OR cancelled-but-not-expired) or Lifetime member
// Checks by user_id first, then by email as fallback
export const isProUser = async (userId, email = null) => {
    if (!isSupabaseConfigured()) return false

    // Get user email if not provided
    let userEmail = email
    if (!userEmail && userId) {
        const { data: { user } } = await supabase.auth.getUser()
        userEmail = user?.email
    }

    // =====================================================
    // CHECK LIFETIME MEMBERSHIP FIRST (never expires)
    // Lifetime members always have Pro access regardless of anything else
    // =====================================================
    if (userId) {
        const { data: lifetimeSub } = await supabase
            .from('subscriptions')
            .select('plan')
            .eq('user_id', userId)
            .eq('plan', 'lifetime')
            .single()

        if (lifetimeSub) {
            syncProStatusToLocalStorage(true)
            return true
        }
    }

    // Also check by email for lifetime (for pre-login check)
    if (userEmail) {
        const { data: lifetimeByEmail } = await supabase
            .from('subscriptions')
            .select('plan, user_id')
            .eq('email', userEmail.toLowerCase())
            .eq('plan', 'lifetime')
            .single()

        if (lifetimeByEmail) {
            // Auto-link user_id if not set
            if (!lifetimeByEmail.user_id && userId) {
                await supabase
                    .from('subscriptions')
                    .update({ user_id: userId })
                    .eq('email', userEmail.toLowerCase())
            }
            syncProStatusToLocalStorage(true)
            return true
        }
    }

    // =====================================================
    // CHECK PRO SUBSCRIPTION (active or cancelled-but-valid)
    // =====================================================

    // Grace period: if next_billing_date is more than this many days in the past,
    // deny Pro access even if status is still 'active' (cron may have missed it)
    const BILLING_GRACE_PERIOD_DAYS = 7

    // First check by user_id for active subscription with plan = 'pro'
    if (userId) {
        const { data: activeSub } = await supabase
            .from('subscriptions')
            .select('status, plan, expires_at, next_billing_date')
            .eq('user_id', userId)
            .eq('status', 'active')
            .eq('plan', 'pro')
            .single()

        if (activeSub) {
            // Safety net: verify the subscription hasn't actually expired
            // If next_billing_date is past the grace period, treat as expired
            if (activeSub.next_billing_date) {
                const billingDate = new Date(activeSub.next_billing_date)
                const graceCutoff = new Date()
                graceCutoff.setDate(graceCutoff.getDate() - BILLING_GRACE_PERIOD_DAYS)
                if (billingDate < graceCutoff) {
                    // Billing date is too far in the past — subscription is effectively expired
                    syncProStatusToLocalStorage(false)
                    return false
                }
            }
            syncProStatusToLocalStorage(true)
            return true
        }

        // Check cancelled-but-not-expired by user_id
        const { data: cancelledSub } = await supabase
            .from('subscriptions')
            .select('status, plan, expires_at')
            .eq('user_id', userId)
            .eq('status', 'cancelled')
            .eq('plan', 'pro')
            .single()

        if (cancelledSub?.expires_at) {
            if (new Date(cancelledSub.expires_at) > new Date()) {
                syncProStatusToLocalStorage(true)
                return true
            }
        }
    }

    // Fallback: Check by email (for subscriptions created before login linking)
    if (userEmail) {
        const { data: emailSub } = await supabase
            .from('subscriptions')
            .select('status, plan, expires_at, next_billing_date, user_id')
            .eq('email', userEmail.toLowerCase())
            .eq('plan', 'pro')
            .single()

        if (emailSub) {
            // If subscription found by email, check if it's active or valid cancelled
            if (emailSub.status === 'active') {
                // Safety net: verify billing date hasn't expired past grace period
                if (emailSub.next_billing_date) {
                    const billingDate = new Date(emailSub.next_billing_date)
                    const graceCutoff = new Date()
                    graceCutoff.setDate(graceCutoff.getDate() - BILLING_GRACE_PERIOD_DAYS)
                    if (billingDate < graceCutoff) {
                        syncProStatusToLocalStorage(false)
                        return false
                    }
                }
                // Link user_id if not set (auto-fix the data)
                if (!emailSub.user_id && userId) {
                    await supabase
                        .from('subscriptions')
                        .update({ user_id: userId })
                        .eq('email', userEmail.toLowerCase())
                }
                syncProStatusToLocalStorage(true)
                return true
            }

            if (emailSub.status === 'cancelled' && emailSub.expires_at) {
                if (new Date(emailSub.expires_at) > new Date()) {
                    syncProStatusToLocalStorage(true)
                    return true
                }
            }
        }
    }

    syncProStatusToLocalStorage(false)
    return false
}

// Helper: Sync Pro status to localStorage and Chrome extension
function syncProStatusToLocalStorage(isPro) {
    try {
        localStorage.setItem('inkspot_is_pro', isPro ? 'true' : 'false')
        localStorage.setItem('inkspot_pro_synced_at', Date.now().toString())

        // Set a data attribute on the document element for easy detection
        document.documentElement.setAttribute('data-inkspot-pro', isPro ? 'true' : 'false')

        // Also sync to Chrome extension via messaging API
        syncProStatusToExtension(isPro)
    } catch (e) {
        // localStorage may not be available
    }
}

// Sync Pro status to Chrome extension using messaging
export async function syncProStatusToExtension(isPro, email = null) {
    const EXTENSION_ID = 'bjifgkinjedaikncmccpihmclmehhmoo'

    try {
        // Get user email if not provided
        let userEmail = email
        if (!userEmail) {
            const { data: { user } } = await supabase.auth.getUser()
            userEmail = user?.email
        }

        // Try to send message to Chrome extension
        if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
            chrome.runtime.sendMessage(EXTENSION_ID, {
                type: 'SYNC_PRO_STATUS',
                isPro: isPro,
                email: userEmail
            }, (response) => {
                if (chrome.runtime.lastError) {
                    // Extension not installed or not responding, ignore
                    console.log('InkSpot: Extension not installed or not responding')
                } else {
                    console.log('InkSpot: Pro status synced to extension:', isPro)
                }
            })
        }
    } catch (e) {
        // Chrome API not available, ignore
    }
}

// Subscribe to auth changes
export const onAuthStateChange = (callback) => {
    return supabase.auth.onAuthStateChange(callback)
}

// Sign in with Google OAuth
export const signInWithGoogle = async () => {
    if (!isSupabaseConfigured()) {
        return { data: null, error: { message: 'Supabase not configured' } }
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
            redirectTo: `${window.location.origin}/auth/callback`,
            queryParams: {
                access_type: 'offline',
                prompt: 'consent',
            }
        }
    })

    return { data, error }
}

// Get full subscription details
export const getSubscriptionDetails = async (userId, email) => {
    if (!isSupabaseConfigured()) return null

    // Try to find by user_id first, then by email
    let query = supabase
        .from('subscriptions')
        .select('*')

    if (userId) {
        query = query.eq('user_id', userId)
    } else if (email) {
        query = query.eq('email', email)
    } else {
        return null
    }

    const { data, error } = await query.single()

    if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription:', error)
    }

    return data
}

// Check subscription expiration status
export const getSubscriptionStatus = (subscription) => {
    if (!subscription) return { status: 'free', daysRemaining: null }

    // Lifetime plans are always active
    if (subscription.plan === 'lifetime') {
        return { status: 'active', daysRemaining: null }
    }

    const now = new Date()
    const nextBilling = subscription.next_billing_date ? new Date(subscription.next_billing_date) : null
    const expiresAt = subscription.expires_at ? new Date(subscription.expires_at) : null

    // If status is explicitly expired, suspended, or past_due
    if (['expired', 'suspended', 'past_due'].includes(subscription.status)) {
        return { status: subscription.status, daysRemaining: 0 }
    }

    // If cancelled but still has access until expiration
    if (subscription.status === 'cancelled' && expiresAt) {
        const daysRemaining = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24))
        if (daysRemaining > 0) {
            return { status: 'expiring', daysRemaining }
        }
        return { status: 'expired', daysRemaining: 0 }
    }

    // If active subscription — also validate billing date hasn't lapsed
    if (subscription.status === 'active') {
        if (nextBilling) {
            const daysUntilBilling = Math.ceil((nextBilling - now) / (1000 * 60 * 60 * 24))
            // If billing date is more than 7 days in the past, treat as expired
            if (daysUntilBilling < -7) {
                return { status: 'expired', daysRemaining: 0 }
            }
            // If billing date is in the past but within grace period, show warning
            if (daysUntilBilling < 0) {
                return { status: 'past_due', daysRemaining: 0 }
            }
            return { status: 'active', daysRemaining: daysUntilBilling }
        }
        return { status: 'active', daysRemaining: null }
    }

    return { status: subscription.status, daysRemaining: null }
}

